import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, Eye, Flag, Map, Play } from 'lucide-react';
import { playClick, playCorrect, playWrong } from '../../utils/sounds';
import { PrecisionStats } from './PrecisionUI';
import MazeBoard from './MazeBoard';
import { adjacentCell, createMaze, getMazeRules, judgeMazeMove, mazeBudget, mazeVisibility, scoreMaze } from './mazeRules';
import type { PrecisionProps, PrecisionResult } from './types';

export default function PrecisionMaze({ mode, tier, onComplete, onFail }: PrecisionProps) {
  const rules = useMemo(() => getMazeRules(mode, tier), [mode, tier]);
  const [board] = useState(() => createMaze(rules.size, rules.checkpoints));
  const budget = useMemo(() => mazeBudget(board, rules), [board, rules]);
  const [phase, setPhase] = useState<'preview' | 'navigate' | 'done'>('preview');
  const [position, setPosition] = useState(board.start);
  const [route, setRoute] = useState([board.start]);
  const [collected, setCollected] = useState(0);
  const [collisions, setCollisions] = useState(0);
  const [scans, setScans] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [remaining, setRemaining] = useState(rules.previewMs);
  const [feedback, setFeedback] = useState('先观察通道与出口，想好路线再出发');
  const activePhase = useRef<'preview' | 'navigate' | 'done'>('preview');
  const pos = useRef(board.start);
  const path = useRef([board.start]);
  const collectedRef = useRef(0);
  const bumps = useRef(0);
  const scanUses = useRef(0);
  const steps = useRef<NonNullable<PrecisionResult['mazeReplay']>['steps']>([]);
  const combo = useRef(0);
  const maxCombo = useRef(0);
  const lastInput = useRef(-Infinity);
  const started = useRef(0);
  const sessionStart = useRef(performance.now());
  const previewStart = useRef(performance.now());
  const finished = useRef(false);
  const scanUntil = useRef(0);
  const settleTimer = useRef<ReturnType<typeof setTimeout>>();
  const finishRef = useRef<(success: boolean, reason?: string) => void>(() => {});
  const callbacks = useRef({ onComplete, onFail });
  callbacks.current = { onComplete, onFail };
  const moveRef = useRef<(dx: number, dy: number) => void>(() => {});

  finishRef.current = (success, reason) => {
    if (finished.current || activePhase.current === 'done') return;
    finished.current = true; activePhase.current = 'done'; setPhase('done');
    const moves = path.current.length - 1;
    const result = scoreMaze(budget.optimal, moves, bumps.current, scanUses.current, success);
    const precision: PrecisionResult = {
      version: 1, tier: rules.level, score: result.score, accuracy: result.accuracy,
      responseMs: null, bestResponseMs: null, maxCombo: maxCombo.current, perfectCount: 0,
      mistakes: bumps.current, total: steps.current.length, trials: [],
      durationSeconds: (performance.now() - sessionStart.current) / 1000, failureReason: reason,
      metrics: [{ label: '实际 / 最短步数', value: `${moves} / ${budget.optimal}` }, { label: '路线效率', value: `${result.efficiency}%` }, { label: '碰壁 / 锁定', value: `${bumps.current} 次` }, { label: '已到地标', value: `${collectedRef.current}/${board.checkpoints.length}` }, { label: '全图查看', value: `${scanUses.current} 次` }, { label: '寻路用时', value: `${((performance.now() - started.current) / 1000).toFixed(1)} 秒` }],
      insights: [moves > budget.optimal ? '有绕行步数：在复盘中对照虚线最短路线，找到可以提前识别的岔路。' : success ? '已走出最短路线，下一次可尝试更少的全图辅助。' : '先记住地标之间的转弯方向，分段规划比一次记下整图更轻松。', '完成得 60 分，路线效率最多 30 分，初始精准分 10 分；碰壁每次扣 3 分，全图查看每次扣 5 分。准确率仅指有效移动占比。'],
      mazeReplay: { size: board.size, passages: board.passages, objectives: [...board.checkpoints, board.exit], optimalPath: board.optimalPath, route: path.current, steps: steps.current },
    };
    setFeedback(success ? '抵达出口，路线已记录' : reason ?? '本局结束');
    const replay = { mechanic: 'maze', precision };
    settleTimer.current = setTimeout(() => success ? callbacks.current.onComplete(result.score, replay) : callbacks.current.onFail(replay), 650);
  };

  const begin = () => {
    if (activePhase.current !== 'preview' || finished.current) return;
    started.current = performance.now(); activePhase.current = 'navigate';
    setRemaining(budget.timeMs); setPhase('navigate');
    setFeedback(board.checkpoints.length ? '按顺序经过编号地标，再抵达旗帜出口' : '沿通道抵达旗帜出口，细线是不能穿过的墙');
  };

  useEffect(() => {
    const timer = setInterval(() => {
      const now = performance.now();
      if (activePhase.current === 'preview') {
        const left = rules.previewMs - (now - previewStart.current);
        setRemaining(Math.max(0, left));
        if (left <= 0) begin();
      } else if (activePhase.current === 'navigate') {
        const left = budget.timeMs - (now - started.current);
        setRemaining(Math.max(0, left));
        if (left <= 0) finishRef.current(false, '寻路时间已用完。先在全图里记住地标与转弯，再尝试出发。');
        if (scanUntil.current && now >= scanUntil.current) { scanUntil.current = 0; setScanning(false); }
      }
    }, 50);
    return () => clearInterval(timer);
  }, [rules, budget]);
  useEffect(() => { finished.current = false; return () => { finished.current = true; clearTimeout(settleTimer.current); }; }, []);

  moveRef.current = (dx, dy) => {
    if (activePhase.current !== 'navigate' || finished.current) return;
    const now = performance.now();
    if (now - started.current >= budget.timeMs) { finishRef.current(false, '寻路时间已用完，本次移动不计入。'); return; }
    if (now - lastInput.current < 115) return;
    lastInput.current = now;
    const from = pos.current, target = adjacentCell(from, dx, dy, board.size);
    const kind = judgeMazeMove(board, from, target, collectedRef.current);
    steps.current.push({ from, to: kind === 'move' ? target! : from, timestamp: Math.round(now - started.current), kind });
    if (kind !== 'move') {
      bumps.current++; combo.current = 0; setCollisions(bumps.current); playWrong();
      setFeedback(kind === 'wall' ? '这边是墙，换个方向看看' : '出口尚未开启，请先按顺序经过所有地标');
      if (bumps.current >= rules.wallLimit) finishRef.current(false, `碰壁或锁定达到 ${rules.wallLimit} 次。观察格子边缘的实线，不要连续试探方向。`);
      return;
    }
    const next = target!;
    pos.current = next; path.current = [...path.current, next]; combo.current++;
    maxCombo.current = Math.max(maxCombo.current, combo.current);
    setPosition(next); setRoute(path.current);
    if (next === board.checkpoints[collectedRef.current]) {
      collectedRef.current++; setCollected(collectedRef.current); playCorrect();
      setFeedback(`地标 ${collectedRef.current} 已到达 · ${collectedRef.current === board.checkpoints.length ? '现在前往出口' : '前往下一个地标'}`);
    } else { playClick(); setFeedback('观察前方通道，不必抢快'); }
    if (next === board.exit && collectedRef.current === board.checkpoints.length) finishRef.current(true);
    else if (path.current.length - 1 >= budget.maxSteps) finishRef.current(false, `已用完 ${budget.maxSteps} 步。复盘时对照最短路线，减少在同一岔路往返。`);
  };
  useEffect(() => {
    const keydown = (e: KeyboardEvent) => {
      const directions: Record<string, [number, number]> = { ArrowUp: [0,-1], ArrowDown: [0,1], ArrowLeft: [-1,0], ArrowRight: [1,0] };
      if (!directions[e.key] || activePhase.current !== 'navigate') return;
      e.preventDefault(); if (!e.repeat) moveRef.current(...directions[e.key]);
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  }, []);
  const scan = () => {
    if (activePhase.current !== 'navigate' || scanUses.current >= rules.scans || scanUntil.current || finished.current) return;
    if (performance.now() - started.current >= budget.timeMs) { finishRef.current(false, '寻路时间已用完。'); return; }
    scanUses.current++; scanUntil.current = performance.now() + 1800;
    setScans(scanUses.current); setScanning(true); setFeedback('全图显示 1.8 秒，计时继续，本次扣 5 分');
  };
  const visible = mazeVisibility(board, position, phase !== 'navigate' || scanning ? null : rules.fogRadius);
  const moves = route.length - 1;

  return <div className="precision-game maze-game">
    <div className="maze-heading"><span><Map size={15} />{phase === 'preview' ? '路线规划' : rules.fogRadius !== null ? '记忆迷雾' : '迷宫寻路'}</span><b>{(remaining / 1000).toFixed(1)}<small> 秒</small></b></div>
    <div className="precision-progress" role="progressbar" aria-label={phase === 'preview' ? '观察剩余时间' : '寻路剩余时间'} aria-valuenow={Math.round(remaining / (phase === 'preview' ? rules.previewMs : budget.timeMs) * 100)} aria-valuemin={0} aria-valuemax={100}><div style={{ width: `${remaining / (phase === 'preview' ? rules.previewMs : budget.timeMs) * 100}%` }} /></div>
    <div className="precision-rule maze-rule"><span>{phase === 'preview' ? '先看全图，记住转弯' : '规划优先，不必抢快'}</span><strong>{phase === 'preview' ? '从起点，走向下一站' : collected < board.checkpoints.length ? `下一站：地标 ${collected + 1}` : '下一站：旗帜出口'}</strong><small>{board.size} × {board.size} 迷宫 · 最短 {budget.optimal} 步 · 最多 {budget.maxSteps} 步</small></div>
    <div className="maze-objectives"><span className="done">起点</span>{board.checkpoints.map((_, i) => <span key={i} className={i < collected ? 'done' : i === collected ? 'active' : ''}>{i + 1} 号地标</span>)}<span className={collected === board.checkpoints.length ? 'active' : ''}><Flag size={11} />出口</span></div>
    <MazeBoard board={board} player={position} visible={visible} collected={collected} visited={route} onCell={cell => {
      const dx = cell % board.size - pos.current % board.size, dy = Math.floor(cell / board.size) - Math.floor(pos.current / board.size);
      if (Math.abs(dx) + Math.abs(dy) === 1) moveRef.current(dx, dy);
    }} />
    <div className="maze-legend"><span><i className="player" />你的位置</span><span><i className="wall" />实线为墙</span><span><i className="exit" />目标地标</span></div>
    {phase === 'preview' ? <button className="maze-begin" onClick={begin}><Play size={16} /> 记好了，开始寻路</button> : <div className="maze-controls"><div className="maze-dpad">{[{ dx: 0, dy: -1, name: '向上', Icon: ArrowUp }, { dx: -1, dy: 0, name: '向左', Icon: ArrowLeft }, { dx: 0, dy: 1, name: '向下', Icon: ArrowDown }, { dx: 1, dy: 0, name: '向右', Icon: ArrowRight }].map(({ dx, dy, name, Icon }) => <button key={name} aria-label={name} disabled={phase !== 'navigate'} onClick={() => moveRef.current(dx,dy)}><Icon size={21} /></button>)}</div>{rules.fogRadius !== null && <button className="maze-scan" disabled={phase !== 'navigate' || scans >= rules.scans || scanning} onClick={scan}><Eye size={18} /><span>查看全图</span><small>{rules.scans - scans} 次 · 扣 5 分</small></button>}</div>}
    <div className="precision-feedback" role="status">{feedback}</div>
    <div className="maze-live-stats"><PrecisionStats stats={[{ label: '已走 / 步数上限', value: `${moves}/${budget.maxSteps}` }, { label: '剩余容错', value: rules.wallLimit - collisions }, { label: '到达地标', value: `${collected}/${board.checkpoints.length}` }]} /></div>
    <p className="precision-note">支持方向键、方向按钮或点相邻格移动。{rules.fogRadius !== null ? '全图观察后仅显示身边一格；查看全图时计时继续。' : '沿通道移动，不能穿过实线墙。'}暂停或切出页面会放弃本局，不记录成绩。</p>
  </div>;
}
